import {  useRef, useState, useEffect} from "react";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  MenuItem,
  LinearProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import FormField from "../../styled/FormField";
import SelectField from "../../styled/SelectField";
import FormLabel from "../../styled/FormLabel";
import IosSwitch from "../../styled/Switch";
import DatePickerField from "../../common/DatePicker";
import _ from "lodash";
import {
  DeleteIconButton,
  DownIcon,
  EyeIconButton,
} from "../../../../assets/common-svg/CommonSvg";
import { UpperIcon } from "../../../assets/CommonSvg";
import FilePreviewModal from "../FilePreview";
import CustomDateRangePicker from "../../../../components/custom-date-picker/CustomDateRangePicker";
import DownloadFiles from '../DownloadFiles/index.js'
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const FormikControl = ({
  label = "",
  type = "text",
  name,
  acceptFormat,
  downloadFile,
  inputClassName,
  removeFile,
  onChange,
  onBlurHandle= () => {},
  formik,
  required = false,
  multiple = false,
  placeholder = "",
  options = [],
  rows = 4,
  format = "DD/MM/YYYY",
  fullWidth = true,
  variant = "standard",
  min = 0,
  max,
  row,
  className = "",
  disabled = false,
  extraNote='',
  boundary=false,
  showMonthYearPicker=false,
  hideLabel = false,
  ...props
}) => {
  const renderField = () => {
    const [open, setOpen] = useState(false);
    const _dynmicFormik = (action) => {
      return _.get(formik[action], name, "");
    };
    const error = _dynmicFormik("touched") && Boolean(_dynmicFormik("errors"));
    const helperText = _dynmicFormik("touched") && _dynmicFormik("errors");
    switch (type) {
      case "text":
      case "email":
      case "number":
      case "time":
      case "password":
        if (props?.tokenizeOnSpace && type === "text") {
          const lastProcessedSubmitCountRef = useRef(0);
          const [isTokenFieldFocused, setIsTokenFieldFocused] = useState(false);
          const rawValue = String(_dynmicFormik("values") ?? "");
          const hasTrailingSeparator = /[\s,]$/.test(rawValue);
          const validateToken =
            props?.tokenValidator ||
            (props?.alphaNumericTokenValidation
              ? (value) => /[A-Za-z]/.test(value) && /[0-9]/.test(value)
              : () => true);
          const tokenValidationMessage =
            props?.tokenValidationMessage ||
            (props?.alphaNumericTokenValidation
              ? "Each batch number must contain at least one letter and one number"
              : "Each value must contain at least one letter and one number");
          const allTokens = rawValue
            .split(/[\s,]+/)
            .map((token) => token.trim())
            .filter(Boolean);
          const committedTokens = hasTrailingSeparator
            ? allTokens
            : allTokens.slice(0, -1);
          const currentToken = hasTrailingSeparator
            ? ""
            : allTokens[allTokens.length - 1] || "";
          const visibleTokens = isTokenFieldFocused ? committedTokens : allTokens;
          const allowChipDeletion = !disabled && !formik?.isSubmitting;

          const serializeTokenizedValue = (
            chips = [],
            inputValue = "",
            preserveTrailingSpace = false
          ) => {
            let nextValue = chips.join(" ");

            if (preserveTrailingSpace && chips.length) {
              nextValue += " ";
            }

            if (inputValue) {
              nextValue += `${nextValue && !nextValue.endsWith(" ") ? " " : ""}${inputValue}`;
            }

            return nextValue;
          };

          const applyTokenizedValue = (
            nextCommittedTokens,
            nextCurrentToken = "",
            preserveTrailingSpace = false,
            invalidTokenFallback = ""
          ) => {
            const invalidCommittedToken = nextCommittedTokens.find(
              (token) => !validateToken(token)
            );
            const nextValue = serializeTokenizedValue(
              nextCommittedTokens,
              nextCurrentToken,
              preserveTrailingSpace
            );

            if (invalidCommittedToken) {
              formik.setFieldTouched(name, true, false);
              formik.setFieldError(name, tokenValidationMessage);
              formik.setFieldValue(
                name,
                serializeTokenizedValue(
                  committedTokens,
                  invalidTokenFallback || nextCurrentToken
                ),
                false
              );
              return;
            }

            formik.setFieldError(name, undefined);
            formik.setFieldValue(name, nextValue, true);
          };

          useEffect(() => {
            const submitCount = formik?.submitCount || 0;
            if (
              submitCount === 0 ||
              submitCount === lastProcessedSubmitCountRef.current
            ) {
              return;
            }

            lastProcessedSubmitCountRef.current = submitCount;
            if (!currentToken) return;

            const finalTokens = [...committedTokens, currentToken];
            const invalidCurrentToken = finalTokens.find(
              (token) => !validateToken(token)
            );

            formik.setFieldTouched(name, true, false);

            if (invalidCurrentToken) {
              formik.setFieldError(name, tokenValidationMessage);
              return;
            }

            formik.setFieldError(name, undefined);
            formik.setFieldValue(
              name,
              serializeTokenizedValue(finalTokens, "", true),
              false
            );
          }, [
            committedTokens,
            currentToken,
            formik,
            formik?.submitCount,
            name,
            tokenValidationMessage,
          ]);

          return (
            <Box sx={{ mt: label ? 3 : 0, width: "100%", display: "block" }}>
              <FormField
                fullWidth
                className={`${inputClassName} ${props?.extraContent ? "_border_right_none" : ""}`}
                autoComplete="off"
                type="text"
                name={name}
                value={isTokenFieldFocused ? currentToken : ""}
                placeholder={
                  !disabled && visibleTokens.length === 0 && !currentToken
                    ? placeholder
                    : ""
                }
                onFocus={() => {
                  if (disabled) {
                    return;
                  }
                  setIsTokenFieldFocused(true);
                }}
                onChange={(e) => {
                  if (onChange) {
                    onChange(e);
                    return;
                  }

                  const incomingValue = e.target.value.replace(/,/g, " ");
                  const incomingHasTrailingSpace = /\s$/.test(incomingValue);
                  const incomingTokens = incomingValue
                    .split(/\s+/)
                    .map((token) => token.trim())
                    .filter(Boolean);
                  const mergedTokens = [...committedTokens, ...incomingTokens];
                  const nextCommittedTokens = incomingHasTrailingSpace
                    ? mergedTokens
                    : mergedTokens.slice(0, -1);
                  const nextCurrentToken = incomingHasTrailingSpace
                    ? ""
                    : mergedTokens[mergedTokens.length - 1] || "";
                  const invalidTokenFallback = incomingHasTrailingSpace
                    ? incomingTokens[incomingTokens.length - 1] || currentToken
                    : nextCurrentToken;

                  applyTokenizedValue(
                    nextCommittedTokens,
                    nextCurrentToken,
                    incomingHasTrailingSpace,
                    invalidTokenFallback
                  );
                }}
                onBlur={(e) => {
                  const finalTokens = currentToken
                    ? [...committedTokens, currentToken]
                    : committedTokens;

                  formik.setFieldTouched(name, true, false);
                  if (
                    currentToken &&
                    !validateToken(currentToken)
                  ) {
                    formik.setFieldError(name, tokenValidationMessage);
                    formik.setFieldValue(
                      name,
                      serializeTokenizedValue(committedTokens, currentToken),
                      false
                    );
                  } else {
                    formik.setFieldError(name, undefined);
                    formik.setFieldValue(
                      name,
                      serializeTokenizedValue(finalTokens, "", true),
                      true
                    );
                  }

                  setIsTokenFieldFocused(false);
                  if (onBlurHandle) {
                    onBlurHandle(e);
                  }
                }}
                error={Boolean(error)}
                helperText={helperText}
                disabled={disabled}
                InputProps={{
                  ...props.InputProps,
                  startAdornment: visibleTokens.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        alignItems: "center",
                        pl: 1,
                        py: 0.75,
                        maxWidth: "100%",
                      }}
                    >
                      {visibleTokens.map((token, index) => (
                        <Chip
                          key={`${token}-${index}`}
                          label={token}
                          size="small"
                          sx={{
                            height: 28,
                            borderRadius: "6px",
                            border: "1px solid #B9CEF8",
                            backgroundColor: "#EAF2FF",
                            color: "#0F172A",
                            fontWeight: 600,
                            "& .MuiChip-label": {
                              px: 1.1,
                              py: 0.25,
                            },
                            "& .MuiChip-deleteIcon": {
                              color: "#0F172A",
                              fontSize: 16,
                              marginRight: "6px",
                            },
                            "& .MuiChip-deleteIcon:hover": {
                              color: "#0F172A",
                            },
                          }}
                          onDelete={
                            allowChipDeletion
                              ? () => {
                                  const nextTokens = visibleTokens.filter(
                                    (_, chipIndex) => chipIndex !== index
                                  );
                                  applyTokenizedValue(nextTokens);
                                }
                              : undefined
                          }
                        />
                      ))}
                    </Box>
                  ) : null,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: committedTokens.length > 0 ? 0.5 : 0,
                    paddingRight: 0,
                  },
                  "& .MuiInputBase-input": {
                    flex: 1,
                    minWidth: "180px",
                    width: "auto",
                    border: "none",
                    backgroundColor: "transparent",
                    paddingLeft: committedTokens.length > 0 ? "4px" : "10px",
                    paddingTop: "10px",
                    paddingBottom: "9px",
                    paddingRight: "18px",
                  },
                }}
              />
              {extraNote ? (
                <Typography
                  variant="body2"
                  color="#FB9D2C"
                  className="absolute -bottom-4 left-0 text-xs"
                >
                  {extraNote}
                </Typography>
              ) : (
                ""
              )}
            </Box>
          );
        }
        return (
          <><FormField
            className={`${inputClassName} ${props?.extraContent ? '_border_right_none':''}`}
            autoComplete="off"
            type={type}
            name={name}
            value={_dynmicFormik("values")}
            placeholder={placeholder}
            // onChange={formik.handleChange}
            onWheel={(e) => { if (type === "number") { e.target.blur() } }}
            onChange={(e) => {
            if (onChange) {
              onChange(e);
              return;
            }
            const { value } = e.target;

           // Allow empty value to support clearing input
           if (value === "") {
             formik.handleChange(e);
             return;
           }
           

          if (props?.isTempratureField) {
            const num = Number(value);
            if (isNaN(num) || num < min || num > max) {
              return; // Prevent change if outside range
            }
          }
        
          // For valid inputs or non-temperature fields
          formik.handleChange(e);
        }}
            onBlur={(e) => {
              if (e.target.value == "" && type == "number") {
                formik.setFieldValue(name, "");
              }
              formik.handleBlur(e);
              if(onBlurHandle)
                onBlurHandle(e)
            }}
            error={Boolean(error)}
            helperText={helperText}
            disabled={disabled}
            InputProps={{
               ...(props?.extraContent && {
            endAdornment: (
              <span className="whitespace-nowrap text-gray-500 pr-1 text-[0.7rem] font-medium">
                {props?.extraContent}
              </span>
            )
           }),
             ...(type === "number" && {
          inputProps: {
            min,
            max,
            step: props?.notDecimal ? 1 : "any",
            inputMode: props?.notDecimal ? "numeric" : "decimal",
            className: props?.notransform ? 'no-transform' : '', 
          },
                onKeyDown: (e) => {
                  if (e.key === " ") {
                    e.preventDefault();
                  }
                  if (type === "number") {
                   if (
      (props?.notDecimal && ["e", "E", ".", "+", "-", " "].includes(e.key)) ||
      (props?.isTempratureField && ["e", "E", "+"].includes(e.key)) ||
      (!props?.isTempratureField && ["e", "E", "-", "+"].includes(e.key)) 
    ) {
                      e.preventDefault();
                    }
                  }
                },
              }),
              ...props.InputProps,
            }}
          />
          {extraNote ?
             <Typography
      variant="body2"
      color="#FB9D2C"
      className="absolute -bottom-4 left-0 text-xs"
    >
      {extraNote}
    </Typography>:''}
          </>
        );
      case "textarea":
        return (
           <FormField
            name={name}
            multiline
            rows={rows}
            value={formik?.values?.[name] ?? ""}
            placeholder={placeholder}
            // onChange={formik.handleChange}
             onChange={(e) => {
              const value = e?.target?.value?.trimStart();
              formik?.setFieldValue(name, value);
            }}
            onBlur={formik.handleBlur}
            error={Boolean(error)}
            helperText={helperText}
            disabled={disabled}
            sx={props.sx}
            className={props.className}
            InputProps={{
              ...props.InputProps,
            }}
          />
        );
      case "phone": {
        const countryCodeName = props?.countryCodeName || "countryCode";
        const combinedPhoneValue = `${formik?.values?.[countryCodeName] || "91"}${_dynmicFormik("values") || ""}`;
        return (
          <div>
            {label ? (
              <Typography
                sx={{
                  color: "black",
                  fontSize: "14px",
                  fontFamily: "roboto",
                  mb: 1,
                }}
              >
                {label}
                {required ? <span style={{ color: "#ff000080" }}> *</span> : null}
              </Typography>
            ) : null}
            <PhoneInput
              country={props?.defaultCountry || "in"}
              value={combinedPhoneValue}
              enableSearch
              containerStyle={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "45px",
                border:"2px solid #e5e7eb",
                paddingLeft: "52px",
                fontSize: "14px",
                color: "#000000",
              }}
              buttonStyle={{
                border: `2px solid #e5e7eb `,
                borderRight: "none",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              }}
              onChange={(value, countryData) => {
                const dialCode = countryData?.dialCode || "";
                const localNumber =
                  dialCode && String(value).startsWith(dialCode)
                    ? String(value).slice(dialCode.length)
                    : String(value || "");
                formik.setFieldValue(countryCodeName, dialCode);
                formik.setFieldValue(name, localNumber);
              }}
              onBlur={() => formik.setFieldTouched(name, true)}
              disabled={disabled}
              placeholder={placeholder}
            />
            {error && <FormHelperText error>{helperText}</FormHelperText>}
          </div>
        );
      }
      case "select":
        return (
          <>
            <SelectField
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            IconComponent={open ? UpperIcon : disabled ? "" : DownIcon}
            name={name}
            className={inputClassName}
            label={"dddd"}
            multiple={multiple}
            displayEmpty
            value={
              (() => {
                const val = _dynmicFormik("values");
                if (val === null || val === undefined || val === "null") {
                  return multiple ? [] : "";
                }
                return val;
              })()
            }
              onChange={(e) => {
                const value = e.target.value;
                if (multiple && Array.isArray(value) && value.includes("")) return;
                if (onChange) onChange(e);
                else {
                  if (multiple && Array.isArray(value)) {
                    const originalValues = value.map(val => {
                      const opt = options.find(o => o.value !== undefined && o.value !== null && String(o.value) === String(val));
                      return opt ? opt.value : val;
                    });
                    formik.setFieldValue(name, originalValues);
                  } else {
                    const opt = options.find(o => o.value !== undefined && o.value !== null && String(o.value) === String(value));
                    formik.setFieldValue(name, opt ? opt.value : value);
                  }
                }
              }}
            onBlur={formik.handleBlur}
            error={Boolean(error)}
            disabled={disabled}
              renderValue={(selected) => {
                const filteredSelected = Array.isArray(selected)
                  ? selected.filter((v) => v !== "" && v !== null && v !== undefined)
                  : [];
                if (!multiple) {
                  if (selected === "" || selected === null || selected === undefined) {
                    return <span style={{ color: "#808080" }}>{placeholder || "-- Select --"}</span>;
                  }
                  const match = options.find((o) => o.value !== undefined && o.value !== null && String(o.value) === String(selected));
                  if (!match) {
                    return <span style={{ color: "#808080" }}>{placeholder || "-- Select --"}</span>;
                  }
                  return match.label;
                }
                if (!filteredSelected || filteredSelected.length === 0) {
                  return <span style={{ color: "#808080" }}>{placeholder || "-- Select --"}</span>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", paddingY: "2px" }}>
                    {filteredSelected.map((val) => {
                      const match = options.find((o) => o.value !== undefined && o.value !== null && String(o.value) === String(val));
                      return (
                        <Chip
                          key={val}
                          label={match ? match.label : val}
                          size="small"
                          onDelete={(e) => {
                            e.stopPropagation();
                            const newValue = filteredSelected.filter((v) => v !== val);
                            const originalNewValue = newValue.map(item => {
                              const opt = options.find(o => o.value !== undefined && o.value !== null && String(o.value) === String(item));
                              return opt ? opt.value : item;
                            });
                            formik.setFieldValue(name, originalNewValue);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                             maxWidth:
                               filteredSelected.length === 1
                                 ? "none !important"
                                 : "150px !important",
                             backgroundColor: '#eff6ff',
                             border: '1px solid #bfdbfe',
                             color: 'primary.main',
                             fontWeight: 600,
                             fontSize: '0.7rem',
                             '& .MuiChip-deleteIcon': {
                               color: 'primary.main',
                               '&:hover': { color: '#ef4444' },
                             },
                          }}
                        />
                      );
                    })}
                  </Box>
                );
              }}
            sx={{
              ...(inputClassName?.includes('fixWidthDropdown') && {
                width: "100%",
                maxWidth: 300 
              }),
              // "& .MuiSelect-select span::before": {
              //   content: multiple ? '""' : `"${placeholder || "-- Select --"}"`,
               //   color: "#808080",
              // },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  ...(inputClassName?.includes('fixWidthDropdown') && {
                    width: '100%',
                    maxWidth: 300,
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }
                  })
                }
              }
            }}
          >
              {!multiple && (
                <MenuItem value="" disabled>
                  <em>{placeholder || "Select"}</em>
                </MenuItem>
              )}
              {multiple && (
                <MenuItem
                  value=""
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const currentValues = (formik.values[name] || []).map(v => String(v));
                    const allValues = options.map((o) => String(o.value));
                    const allSelected = allValues.every((v) => currentValues.includes(v));
                    const targetValues = allSelected ? [] : options.map(o => o.value);
                    formik.setFieldValue(name, targetValues);
                  }}
                >
                  <Checkbox
                    checked={
                      Array.isArray(formik.values[name]) &&
                      options.length > 0 &&
                      formik.values[name].length === options.length
                    }
                    indeterminate={
                      Array.isArray(formik.values[name]) &&
                      formik.values[name].length > 0 &&
                      formik.values[name].length < options.length
                    }
                  />
                  <span style={{ fontWeight: 500 }}>Select All</span>
                </MenuItem>
              )}
            

            {options.map((option) => (
              <MenuItem key={option.value} value={option.value !== undefined && option.value !== null ? String(option.value) : ""}>
                {option.label}
              </MenuItem>
            ))}
          </SelectField>
            {error && <FormHelperText error>{helperText}</FormHelperText>}
            {extraNote ?
             <Typography
      variant="body2"
      color="#FB9D2C"
      className="absolute -bottom-4 left-0 text-xs"
    >
      <b>Note:</b>: {extraNote}
    </Typography>:''}
          </>
        );
      case "radio":
        return (
          <>
            <RadioGroup
              name={name}
              value={_dynmicFormik("values")}
              onChange={formik.handleChange}
              className={label ? "mt-3" : ""}
              row={row ?? true}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  name={name}
                  value={option.value}
                  disabled={disabled}
                  control={<Radio color="info" />}
                  label={option.label}
                  className="mr-5 mb-0"
                />
              ))}
            </RadioGroup>
            {error && <FormHelperText error>{helperText}</FormHelperText>}
          </>
        );
    case "checkbox":
case "switch":
  if (!options || options.length === 0) {
    return (
      <FormControlLabel
        control={
          type === "checkbox" ? (
            <Checkbox
              name={name}
              checked={formik.values[name] || false}
              onChange={(e) => {
                formik.setFieldValue(name, e.target.checked);
                if (onChange) onChange(e);
              }}
          sx={{
           "& .MuiSvgIcon-root": {
             fontSize: 22,
             borderRadius: "6px !important",
           },
            color: "#cbd5e1",
            "&.Mui-checked .MuiSvgIcon-root": {
             color: "#3b82f6", 
             borderRadius: "6px !important",
            },
          }}
            />
          ) : (
            <IosSwitch
              name={name}
              checked={formik.values[name] || false}
              onChange={(e) => {
                formik.setFieldValue(name, e.target.checked);
                if (onChange) onChange(e);
              }}
            />
          )
        }
        label={label}
        disabled={disabled}
        sx={{
          "& .MuiFormControlLabel-label": {
            fontSize: "16px",  
          },
        }}
        className={`${boundary ? 'w-full border border-gray-300 rounded-lg px-2 py-1 mt-3' : ''}`}
      />
    );
  }

  return (
    <>
      <FormGroup className={`${label ? "mt-3" : ""}`} row>
        {options.map((option) => {
          const props = {
            color: "info",
            name: name,
            value: option.value,
            checked:
              option?.checked ||
              _dynmicFormik("values")?.includes(option.value),
            onChange: (e) => {
              const { checked } = e.target;
              const currentValues = _dynmicFormik("values") || [];

              const newValues = checked
                ? [...currentValues, option.value]
                : currentValues.filter((v) => v !== option.value);

              formik.setFieldValue(name, newValues);
              if (onChange) onChange(e);
            },
          };

          return (
            <FormControlLabel
              key={option.value}
              control={
                type === "checkbox" ? (
                  <Checkbox {...props} />
                ) : (
                  <IosSwitch {...props} />
                )
              }
              disabled={disabled}
              label={option.label}
               className={`mr-4 mb-3 pt-2 ${
    boundary
      ? "w-full border border-gray-300 rounded-lg px-2 py-1 mt-3 items-center"
      : ""
  }`}  
            />
          );
        })}
      </FormGroup>
      {error && <FormHelperText error>{helperText}</FormHelperText>}
    </>
  );
      case "date":
        return (
          <div className="relative">
            <DatePickerField
              className={inputClassName}
              handleChange={(date) =>
                onChange || formik.setFieldValue(name, date)
              }
              label={label}
              value={_dynmicFormik("values")}
              InputId={name}
              InputName={name}
              placeholder={placeholder}
              disabled={disabled}
              minDate={min}
              maxDate={max}
              showMonthYearPicker={showMonthYearPicker}
              format={format}
              onBlur={(date) => onChange || formik.setFieldValue(name, date)}
             {...((props.position  || name === 'apheresis_date' || name === 'infusion_date' || name === 'completed_date')? {
              popperContainer: ({ children }) => (
            <div style={{ position: props.position === 'top' ? 'absolute' : 'fixed',
              top: props.position === 'top' ? 0 : undefined,
                  zIndex: 1600
               }}
              >
              {children}
            </div>
          )
        } : {})}
                />
            {(Boolean(error) || helperText) && (
              <span className="absolute top-[4.3rem] input_error">
                {helperText}
              </span>
            )}
          </div>
        );

      case "date-range":
        return (
           <div className="relative">
            <CustomDateRangePicker
              InputId={name}
              value={_dynmicFormik("values")}
              selectedDate={_dynmicFormik("values")}
              disabled={disabled}
              minDate={min}
              maxDate={max}
              format={format}
              handleDateChange={(range) => {
                formik?.setFieldValue(name, range);
              }}
              hidePresets={formik?.isHidePresets}
              centeredPicker={formik?.isCenteredPicker}
              months={props?.months}
            />
            {(Boolean(error) || helperText) && (
              <span className="absolute top-[4.3rem] input_error">
                {helperText}
              </span>
            )}
            </div>
          );
														
      case "file": {
        const fileValues = _dynmicFormik("values");
        const fileInputRef = useRef(null);
        const [isUploading, setIsUploading] = useState(false);
        const [previewOpen, setPreviewOpen] = useState(false);
        const [filePreview, setFilePreview] = useState("");
        const hasError = _dynmicFormik("touched") && Boolean(_dynmicFormik("errors"));
        const handleDownload = async (id) => {
          const url = await downloadFile(id);
          setFilePreview(url);
          setPreviewOpen(true);
        };

        useEffect(() => {
          if (hasError) {
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
        }, [hasError]); 

        return (
          <div className="mt-6">
              <>
                <input
                  type="file"
                  name={name}
                  multiple={multiple}
                  ref={fileInputRef}
                  onChange={async (e) => {
                    setIsUploading(true);
                    try {
                      const files = Array.from(e.target.files);
                      if (onChange) {
                        await onChange(e); // support async
                      } else {
                        if (multiple) {
                          const existingFiles = fileValues || [];
                          formik.setFieldValue(name, [
                            ...existingFiles,
                            ...files,
                          ]);
                        } else {
                          formik.setFieldValue(name, files[0]);
                        }
                      }
                    } finally {
                      setIsUploading(false);
                    }
                  }}
                  style={{
                    ...(disabled && {
                      backgroundColor: "#F0F1F5",
                      color: "#5c5959",
                    }),
                    border: "1px solid",
                    padding: "18px",
                  }}
                  onBlur={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      formik.handleBlur(e);
                    }
                  }}
                  disabled={disabled}
                  accept={acceptFormat?.join(",")}
                  className={`border w-100 rounded-md p-2 border-gray-300
                      file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold
                      ${
                        disabled
                          ? "file:bg-[#F0F1F5] file:text-[#5c5959]"
                          : "file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      }
                      ${inputClassName}`}
                  {...props}
                />
               {fileValues?.length !== 0 && disabled &&
             (
             ( fileValues?.length || fileValues?.file_name) ? '' :
             <h5 className="font-medium mt-1"> No File Uploaded</h5>
            )}
                {/* Loader */}
                {isUploading && (
                  <div className="mt-2 flex items-center gap-2 text-blue-600">
                    <div className="w-100">
                      <LinearProgress color="info" />
                    </div>
                    <span>Uploading...</span>
                  </div>
                )}
              </>
            {fileValues && downloadFile && (
              <div>
                {Array.isArray(fileValues)
                  ? fileValues?.map((file, index) => (
                      file ? 
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-100 p-2 rounded mb-1"
                      >
                        <span className="truncate max-w-xs">
                          {file?.name || file?.file_name} (
                          {Math.round(file?.size || file?.file_name / 1024)} KB)
                        </span>
                        <div className="flex gap-2">
                          <div
                            className={
                              disabled ? " disable-add-visit-btn" : " "
                            }
                          >
                            <Tooltip title="Delete">
                              <span>
                                <IconButton
                                  disabled={disabled}
                                  onClick={() => {
                                    fileInputRef.current.value = null
                                    if (removeFile) {
                                      removeFile(name, file?.id);
                                    } else {
                                      const newFiles = [...fileValues];
                                      newFiles.splice(index, 1);
                                      formik.setFieldValue(
                                        name,
                                        newFiles.length ? newFiles : []
                                      );
                                    }
                                  
                                  }}
                                >
                                  <DeleteIconButton
                                    bgColor={"#FA5151"}
                                    disabled={disabled}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                          {file?.id && ( <>
                            <Tooltip title="View">
                              <span>
                                <IconButton
                                  onClick={() => handleDownload(file?.id)}
                                >
                                  <EyeIconButton bgColor={"#3571FF"} />
                                </IconButton>
                              </span>
                            </Tooltip>
                             <DownloadFiles
                        fileId={file?.id}
                        fileName={file?.file || file?.file_name}
                      /></>
                          )}
                        </div>
                      </div>:''
                    ))
                  : fileValues && (
                      <div className="flex mt-2 items-center justify-between bg-gray-100 p-2 rounded">
                        <span className="truncate max-w-xs">
                          {fileValues?.name || fileValues?.file_name} (
                          {Math.round(
                            fileValues?.size || fileValues?.file_size / 1024
                          )}{" "}
                          KB)
                        </span>
                        <div className="flex gap-2">
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                disabled={disabled}
                                onClick={() => {
                                  fileInputRef.current.value = null;
                                  if (removeFile) {
                                    removeFile(name, fileValues?.id);
                                  } else {
                                    formik.setFieldValue(name, null);
                                  }
                                }}
                              >
                                <DeleteIconButton
                                  bgColor={"#FA5151"}
                                  disabled={disabled}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {fileValues?.id && ( <>
                            <Tooltip title="View">
                              <span>
                                <IconButton
                                  onClick={() => handleDownload(fileValues?.id)}
                                >
                                  <EyeIconButton bgColor={"#3571FF"} />
                                </IconButton>
                              </span>
                            </Tooltip>
                             <DownloadFiles
                        fileId={fileValues?.id}
                        fileName={fileValues?.file || fileValues?.file_name}
                        iconColor="#2F5597"
                      />
                         </> )}
                        </div>
                      </div>
                    )}
              </div>
            )}
            <FilePreviewModal
              ModalTitle={"File Preview"}
              setOpen={setPreviewOpen}
              open={previewOpen}
              fileURL={filePreview}
            />
            {error && <FormHelperText error>{helperText}</FormHelperText>}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <FormControl variant={variant} fullWidth={fullWidth} className={className}>
  {!hideLabel &&
  !((type === "checkbox" || type === "switch") && (!options || options.length === 0)) &&
  label &&
  type !== "phone" && (
    <FormLabel shrink required={required}>
      {label}
    </FormLabel>
)}
      {renderField()}
    </FormControl>
  );
};

export default FormikControl;
